import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { UserContext } from "../../../shared/context/userContext";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPBanner from "../../../shared/components/EMPBanner/EMPBanner";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import CheckMarkCircleIcon from "../../../shared/Icons/CheckMarkCircleIcon";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  mapProgressAdvertisedPlanPricingToRadioSelection,
} from "../../../shared/utils/onboardingProgressUtil";
import styles from "./OnboardingAdvertisedPlanPricing.module.scss";

const TEST_ID = "advertised-plan-pricing";
const I18N_BASE = "DealerOnboarding.content.advertisedPlanPricing";

const PARAGRAPHS = [
  { key: "descriptionOne", testSuffix: "description-one" },
  { key: "descriptionTwo", testSuffix: "description-two" },
];

const CHOICES = [
  { value: "Yes", labelKey: "radioYes", testSuffix: "radio-yes" },
  { value: "No", labelKey: "radioNo", testSuffix: "radio-no" },
];

const OnboardingAdvertisedPlanPricing = ({
  savedAdvertisedPlanPricingPreference = "",
}) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const brandName = isFordBrand ? "Ford" : "Lincoln";
  const brandClass = isFordBrand ? "ford" : "lincoln";
  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;
  const updatedBy = user?.userId || user?.commonName;

  const [selection, setSelection] = useState("");
  const [savedSelection, setSavedSelection] = useState("");
  const [saveError, setSaveError] = useState(null);
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const savedSelectionRef = useRef(savedSelection);
  savedSelectionRef.current = savedSelection;

  useEffect(() => {
    const mapped = mapProgressAdvertisedPlanPricingToRadioSelection(
      savedAdvertisedPlanPricingPreference
    );
    const baselineAtSync = savedSelectionRef.current;
    setSelection((current) => (current === baselineAtSync ? mapped : current));
    setSavedSelection(mapped);
  }, [savedAdvertisedPlanPricingPreference]);

  const hasPendingChange = selection !== savedSelection;

  const mutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.saveAdvertisedPlanPricingPreference(
        dealerCommonId,
        payload,
        user?.token
      ),
    onSuccess: (_data, variables) => {
      setSavedSelection(variables.advertisedPlanPricingPreference);
      setSaveError(null);
      setShowSavedBanner(true);
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
    },
    onError: () => {
      setShowSavedBanner(false);
      setSaveError(t(`${I18N_BASE}.saveError`));
    },
  });

  const handleSelect = (event) => {
    setSelection(event.target.value);
    setSaveError(null);
    setShowSavedBanner(false);
  };

  const handleSave = () => {
    if (!selection || !dealerCommonId || !hasPendingChange) {
      return;
    }
    mutation.mutate({
      advertisedPlanPricingPreference: selection,
      updatedBy,
    });
  };

  const handleCancel = () => {
    setSelection(savedSelection);
    setSaveError(null);
    setShowSavedBanner(false);
  };

  const saveButtonLabel = useMemo(() => {
    if (mutation.isLoading) {
      return t(`${I18N_BASE}.saving`);
    }
    if (showSavedBanner && !hasPendingChange) {
      return t(`${I18N_BASE}.saved`);
    }
    return t(`${I18N_BASE}.saveButtonLabel`);
  }, [mutation.isLoading, showSavedBanner, hasPendingChange, t]);

  return (
    <div className={`tw-bg-white tw-rounded-lg tw-p-8 ${brandClass}`}>
      <OnboardingPageTitle testId={`${TEST_ID}-title`} className="tw-mb-6">
        {t(`${I18N_BASE}.title`)}
      </OnboardingPageTitle>

      <Box className={styles.contentContainer}>
        {showSavedBanner && (
          <div
            className={styles.banner}
            data-testid={`${TEST_ID}-success-banner`}
          >
            <EMPBanner
              severity="success"
              title={t(`${I18N_BASE}.saveSuccessful`)}
              icon={<CheckMarkCircleIcon fill="#008200" />}
            />
          </div>
        )}

        {saveError && (
          <div className={styles.banner} data-testid={`${TEST_ID}-save-error`}>
            <EMPBanner severity="error" title={saveError} />
          </div>
        )}

        <Box className={styles.descriptionContainer}>
          {PARAGRAPHS.map(({ key, testSuffix }) => (
            <Typography
              key={key}
              component="p"
              className={styles.bodyText}
              data-testid={`${TEST_ID}-${testSuffix}`}
            >
              {t(`${I18N_BASE}.${key}`, { currentBrand: brandName })}
            </Typography>
          ))}
        </Box>

        <Box className={styles.selectionContainer}>
          <Typography
            component="p"
            className={styles.question}
            data-testid={`${TEST_ID}-question`}
          >
            {t(`${I18N_BASE}.question`, { currentBrand: brandName })}
          </Typography>

          <FormControl component="fieldset">
            <RadioGroup
              name={TEST_ID}
              value={selection}
              onChange={handleSelect}
              data-testid={`${TEST_ID}-radio-group`}
              className={styles.radioGroup}
            >
              {CHOICES.map(({ value, labelKey, testSuffix }) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={
                    <Radio className={styles.radioButton} disableRipple />
                  }
                  label={t(`${I18N_BASE}.${labelKey}`)}
                  data-testid={`${TEST_ID}-${testSuffix}`}
                  className={styles.radioLabel}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        <Box className={styles.saveButtonContainer}>
          <EMPButton
            variant="contained"
            disabled={!selection || !hasPendingChange || mutation.isLoading}
            onClick={handleSave}
            isFord={isFordBrand}
            testId={`${TEST_ID}-save-button`}
            className={styles.saveButton}
          >
            {saveButtonLabel}
          </EMPButton>

          {Boolean(savedSelection) && hasPendingChange && (
            <Button
              variant="text"
              onClick={handleCancel}
              data-testid={`${TEST_ID}-cancel-button`}
              className={styles.cancelButton}
            >
              {t(`${I18N_BASE}.cancel`)}
            </Button>
          )}
        </Box>
      </Box>
    </div>
  );
};

OnboardingAdvertisedPlanPricing.propTypes = {
  savedAdvertisedPlanPricingPreference: PropTypes.string,
};

export default OnboardingAdvertisedPlanPricing;
